<template>
  <div>
    <div id="title-panel">
      <div id="title-container">
        <h2>{{ t('projectsSuppliers.suppliers') }}</h2>
        <div id="suppliers-counter">{{ suppliers?.length ?? 0 }}</div>
      </div>

      <p id="description">
        {{ t('projectsSuppliers.description') }}
      </p>

      <vf-button
        id="add-supplier-button"
        :size="buttonSizes.large"
        :variant="buttonVariants.secondary"
        @click="handleAddSupplierClick"
      >
        {{ t('projectsSuppliers.addSupplier') }}
      </vf-button>
    </div>

    <table-wrapper
      v-model:page-options="pageOptions"
      v-model:filters="filters"
      action-icon="delete"
      :fields="tableFields"
      :filter-configuration="filterConfiguration"
      :items="suppliers"
      :loading="isLoading"
      :mode="TableMode.CLIENT_SIDE"
      row-clickable
      @action-icon-click="handleRemoveSupplierIconClick"
      @row-click="handleRowClick"
    />

    <add-supplier-modal
      ref="modalRef"
      :project-id="projectData._id"
      @supplier-added="fetchSuppliers"
    />
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '../../composables/useToast';
  import axios, { AxiosError, AxiosResponse, HttpStatusCode } from 'axios';
  import { useRouter } from 'vue-router';
  import endpointsList from '../../helpers/endpointsList';
  import { IProjectDetailsDto, IProjectsSupplierSummary } from 'types';
  import AddSupplierModal from '../AddSupplierModal/AddSupplierModal.vue';
  import {
    TableFieldsBuilder,
    TableFiltersBuilder,
    TableMode,
    useTableWrapper,
  } from '@/components/TableWrapper';
  import { Route } from '@/routes';
  import { routeParams } from '@/routes';

  const props = defineProps<{ projectData: IProjectDetailsDto }>();
  const { t } = useI18n();
  const { createSuccessToast, createErrorToast } = useToast();
  const router = useRouter();

  const suppliers = ref<IProjectsSupplierSummary[]>([]);
  const TABLE_NAMESPACE = 'ps';
  const { pageOptions, filters, isLoading } =
    useTableWrapper<IProjectsSupplierSummary>(
      TableMode.CLIENT_SIDE,
      'supplierName',
      fetchSuppliers,
      undefined,
      true,
      TABLE_NAMESPACE,
    );

  const filterConfiguration = new TableFiltersBuilder<IProjectsSupplierSummary>(
    {
      translationKey: 'projectsSuppliers',
    },
  )
    .addInputFilter('supplierName', { width: '208px' })
    .addInputFilter('assignedContentTypeNames', { width: '208px' })
    .addInputFilter('assignedCoworkerNames', {
      width: '208px',
      customLabel: t('projectsSuppliers.coworkers'),
    })
    .build();

  const tableFields = computed(() =>
    new TableFieldsBuilder<IProjectsSupplierSummary>(
      {
        sortable: true,
        labelTranslationKey: 'projectsSuppliers',
      },
      { actionIcon: true },
    )
      .addField('supplierName', {
        width: '30%',
      })
      .addField('assignedContentTypeNames', {
        width: '30%',
        valueGetter: (item) => contentTypesForSupplier(item.supplierId),
      })
      .addField('assignedCoworkerNames', {
        width: '30%',
        valueGetter: (item) => coworkersForSupplier(item.supplierId),
        label: t('projectsSuppliers.coworkers'),
      })
      .addField('uploadCount', {
        width: '10%',
        label: t('projectsSuppliers.deliveries'),
      })
      .build(),
  );

  async function fetchSuppliers() {
    try {
      isLoading.value = true;
      const response: AxiosResponse<IProjectsSupplierSummary[]> =
        await axios.get(
          endpointsList.projectsSuppliers.getProjectsSuppliersSummary(
            props.projectData._id?.toString(),
          ),
        );
      suppliers.value = response.data;
    } catch {
      createErrorToast(t('projectsSuppliers.errors.couldNotLoadSuppliers'));
    } finally {
      isLoading.value = false;
    }
  }

  const modalRef = ref<InstanceType<typeof AddSupplierModal> | null>(null);
  function handleAddSupplierClick() {
    modalRef.value?.show();
  }

  function contentTypesForSupplier(supplierId: string): string {
    if (!suppliers.value) {
      return '';
    }
    const supplier = suppliers.value.find(
      (supplier) => supplier.supplierId === supplierId,
    );
    const contentTypeNames = supplier?.assignedContentTypeNames;
    return contentTypeNames?.join(', ') ?? '';
  }

  function coworkersForSupplier(supplierId: string): string {
    if (!suppliers.value) {
      return '';
    }
    const supplier = suppliers.value.find(
      (supplier) => supplier.supplierId === supplierId,
    );
    const assignedCoworkerNames = supplier?.assignedCoworkerNames;
    return assignedCoworkerNames?.join(', ') ?? '';
  }

  function handleRowClick(supplier: IProjectsSupplierSummary) {
    router.push({
      name: Route.PROJECT_SUPPLIER_DETAILS,
      params: {
        [routeParams[Route.PROJECT_SUPPLIER_DETAILS].projectId]:
          props.projectData._id.toString(),
        [routeParams[Route.PROJECT_SUPPLIER_DETAILS].supplierId]:
          supplier.supplierId,
      },
    });
  }

  async function handleRemoveSupplierIconClick(
    supplier: IProjectsSupplierSummary,
  ) {
    try {
      isLoading.value = true;
      await axios.delete(
        endpointsList.projectsSuppliers.removeSupplierFromProject(
          props.projectData._id.toString(),
          supplier.supplierId,
        ),
      );
      createSuccessToast(t('projectsSuppliers.supplierSuccessfullyDeleted'));
    } catch (err) {
      if (
        err instanceof AxiosError &&
        err.response?.status === HttpStatusCode.Conflict
      ) {
        createErrorToast(
          t('projectsSuppliers.errors.cannotRemoveSupplierWithUploads'),
        );
      } else {
        createErrorToast(t('projectsSuppliers.errors.errorRemovingSupplier'));
      }
    } finally {
      isLoading.value = false;
      fetchSuppliers();
    }
  }
</script>

<style scoped lang="scss">
  #title-panel {
    margin-top: 27px;
    margin-bottom: 50px;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 2fr 1fr;
    align-items: center;
    justify-items: center;

    #title-container {
      justify-self: flex-start;
      display: flex;
      align-items: flex-start;

      h2 {
        @include subheading;
      }
      #suppliers-counter {
        margin-left: 10px;
        background-color: black;
        color: white;
        font-size: 12px;
        border-radius: 50px;
        padding: 3px 20px;
      }
    }

    p#description {
      margin-bottom: 0;
      max-height: 62px;
      width: 645px;
      font-size: 16px;
      line-height: 28px;
      font-weight: 500;
    }

    button#add-project-button {
      justify-self: end;
    }
  }

  @include table-action-icon-on-hover;

  #action-icon {
    cursor: pointer;
  }

  :deep(.row-class) {
    cursor: pointer;
  }
</style>
